import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  OnInit,
  computed,
  ViewChild,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatTabGroup } from "@angular/material/tabs";
import { ActivatedRoute } from "@angular/router";

import { firstValueFrom } from "rxjs";

import { EudrDdsPageService } from "@components/reports/eudr/dds/eudr-dds-page.service";
import { ReportsEudrModel } from "@components/reports/eudr/reports-eudr.model";
import { ConfirmDialogComponent } from "@components/shared";
import { BackendErrorConstants, IconConstants, TextConstants } from "@shared/constants";
import {
  AttachmentTypeEnum,
  ConfirmDialogResponseEnum,
  ExtensionProviderEnum,
  RouteEnum,
} from "@shared/enums";
import { IDelivery } from "@shared/interfaces";
import { EudrDds } from "@shared/interfaces/eudr-dds.interface";
import {
  NotificationService,
  AuthenticationService,
  CommonService,
  DeliveriesService,
  EudrExtensionService,
  AttachmentsService,
  DeleteErrorHandlerService,
  ExtensionsService,
} from "@shared/services";
import { EudrDdsService } from "@shared/services/api/eudr-dds.service";
import { RouterService } from "@shared/services/router.service";
import { CommonUtils, LocalizationUtils } from "@shared/utils";

@Component({
  standalone: false,
  selector: "app-reports-eudr-dds",
  templateUrl: "./reports-eudr-dds.component.html",
  styleUrls: ["./reports-eudr-dds.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportsEudrDdsComponent implements OnInit {
  public readonly translations: any = {
    delete: TextConstants.DELETE,
    eudrCredentialsTooltip: $localize`:@@dds.addeucredentials:Please add EU Information System credentials first`,
    submit: $localize`:@@action.dds.submit:Submit`,
    submitting: $localize`:@@dds.submitting:Submitting...`,
    referenceNumbersStatus: $localize`:@@dds.help:1. Reference numbers & Status`,
    referenceNumber: $localize`:@@eudr.label.referencenumber:1. Reference number`,
    internalReferenceNumber: $localize`:@@shared.internalreferencenumber:Internal reference number`,
    status: TextConstants.STATUS,
    tracesReferenceNumber: $localize`:@@shared.tracesreferencenumber:TRACES reference number`,
    tracesVerificationNumber: $localize`:@@shared.tracesverificationnumber:TRACES verification number`,
    activity: $localize`:@@eudr.label.activity:2. Activity`,
    operatorTrader: $localize`:@@eudr.label.operatordetails:3. Operator / Trader name and address`,
    placeOfActivity: $localize`:@@eudr.label.placeofactivity:4. Place of activity`,
    countryOfExit: $localize`:@@shared.countryofexit:Country of exit`,
    isoCode: $localize`:@@dds.isocode:ISO code`,
    countryOfActivity: $localize`:@@shared.countryofactivity:Country of activity`,
    countryOfEntry: $localize`:@@shared.countryofentry:Country of entry`,
    additionalInfo: $localize`:@@dds.additionalinfo.help:5. Additional information`,
    totals: $localize`:@@shared.totals:Totals`,
    netMass: TextConstants.NET_MASS_KG,
    volume: TextConstants.VOLUME_M3,
    totalArea: $localize`:@@shared.totalareaha:Total area (ha)`,
    commodities: $localize`:@@label.commodityiesorproducts:6. Commodity(-ies) or Product(-s)`,
  };

  public statement = signal<EudrDds.DdsDetails>(null);

  public extensionsService = inject(ExtensionsService);

  public ddsReference = signal<EudrDds.DdsReference>(null);

  public delivery = signal<IDelivery>(null);

  public isLoading = signal<boolean>(true);

  public isSubmitting = signal<boolean>(false);

  public hasEudrCredentials = signal<boolean>(false);

  public countryOfActivityName = signal<string>("");

  public borderCrossCountryName = signal<string>("");

  public hasStatusUpdateError = signal<boolean>(false);

  public hsCodes = signal<ReportsEudrModel.IHsCode[]>([]);

  public statusDisplayText = signal<string>("");

  public activityTypeDisplayText = signal<string>("");

  public title = computed(() => `Delivery ${this.delivery().deliveryId} due diligence statement`);

  private readonly eudrDdsService: EudrDdsService = inject(EudrDdsService);

  private readonly deliveriesService: DeliveriesService = inject(DeliveriesService);

  private readonly route: ActivatedRoute = inject(ActivatedRoute);

  private readonly commonService: CommonService = inject(CommonService);

  private readonly routerService: RouterService = inject(RouterService);

  private readonly notificationService: NotificationService = inject(NotificationService);

  public readonly eudrDdsPageService: EudrDdsPageService = inject(EudrDdsPageService);

  private readonly eudrExtensionService: EudrExtensionService = inject(EudrExtensionService);

  private readonly authenticationService: AuthenticationService = inject(AuthenticationService);

  private readonly attachmentsService: AttachmentsService = inject(AttachmentsService);

  private readonly deleteErrorHander = inject(DeleteErrorHandlerService);

  private readonly httpClient: HttpClient = inject(HttpClient);

  private readonly dialog: MatDialog = inject(MatDialog);

  public readonly hasEntitiesWriteAccess: boolean =
    this.authenticationService.hasEntitiesWriteAccess;

  public readonly operatorSectionText = ReportsEudrModel.operatorSectionText;

  public isPending = computed(() => this.ddsReference().status === EudrDds.StatusEnum.PENDING);

  public isSubmitted = computed(() => this.ddsReference().status === EudrDds.StatusEnum.SUBMITTED);

  public isExportActivity = computed(
    () => this.statement()?.statement.activityType === ReportsEudrModel.ActivityEnum.EXPORT,
  );

  public statusTagClass = computed(() =>
    this.eudrDdsPageService.getStatusTagColor(this.ddsReference().status),
  );

  public isReferenced = computed(
    () => !this.statement() || this.statement().type === EudrDds.TypeEnum.EXISTING_DDS_IDENTIFIER,
  );

  public readonly statusEnum: typeof EudrDds.StatusEnum = EudrDds.StatusEnum;

  @ViewChild("tabGroup") tabGroup!: MatTabGroup;

  public readonly serviceUnavailableMsg = TextConstants.SERVICE_UNAVAILABLE;

  public isDdsServiceUnavailable = signal<boolean>(false);

  public readonly activeOrganisationId = this.authenticationService.getActiveOrganisationId();

  private isEudrExtensionEnabled = signal<boolean>(true);

  public async onLoadDdsReference(): Promise<EudrDds.DdsReference> {
    try {
      const ddsReferenceId = this.route.snapshot.params["id"];

      return await this.eudrDdsService.get(ddsReferenceId);
    } catch (error) {
      switch (error?.status) {
        case 400:
        case 404:
          this.notificationService.showError(BackendErrorConstants.NOT_FOUND);
          break;
        case 503:
          this.isDdsServiceUnavailable.set(true);
          break;
        default:
          this.notificationService.showError(TextConstants.DEFAULT_ERROR_NOTIFICATION);
          break;
      }

      return null;
    }
  }

  public async onSavedReferences(): Promise<void> {
    this.isLoading.set(true);

    const [ddsReference, statement] = await Promise.all([
      this.onLoadDdsReference(),
      this.loadStatement(this.ddsReference().id),
    ]);

    if (!ddsReference) {
      return;
    }

    this.setAdditionalValues(statement, ddsReference);

    this.isLoading.set(false);
  }

  private async loadDelivery(): Promise<IDelivery> {
    const attachments = await this.attachmentsService.getAll(
      AttachmentTypeEnum.DDS_REFERENCE,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      "DELIVERY",
    );

    const delivery = await this.deliveriesService.get(
      CommonUtils.getUriId(attachments[0].targetUri),
    );

    return delivery;
  }

  private async loadStatement(ddsReferenceId: string): Promise<EudrDds.DdsDetails> {
    if (!this.isEudrExtensionEnabled()) {
      return null;
    }
    try {
      return await this.eudrDdsService.getStatement(ddsReferenceId);
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 404) {
        return null;
      }

      throw error;
    }
  }

  public async ngOnInit(): Promise<void> {
    const ddsReferenceId = this.route.snapshot.params["id"];

    if (!ddsReferenceId) {
      this.notificationService.showError(BackendErrorConstants.NOT_FOUND);

      return;
    }

    const fetchedDdsReference = await this.onLoadDdsReference();
    const enabledExtensions = await this.extensionsService.getAllEnabled(this.activeOrganisationId);

    this.isEudrExtensionEnabled.set(enabledExtensions.includes(ExtensionProviderEnum.EUDR));

    if (!fetchedDdsReference) {
      return;
    }

    const [hsCodes, delivery, statement] = await Promise.all([
      firstValueFrom(
        this.httpClient.get<ReportsEudrModel.IHsCode[]>(ReportsEudrModel.hsCodesJsonPath),
      ),
      this.loadDelivery(),
      this.loadStatement(fetchedDdsReference.id),
      this.checkEudrCredentials(),
    ]);

    let ddsReference = fetchedDdsReference;

    if (this.hasEntitiesWriteAccess && ddsReference.status !== EudrDds.StatusEnum.PENDING) {
      try {
        ddsReference = await this.eudrDdsService.updateStatus(ddsReferenceId);
      } catch {
        this.hasStatusUpdateError.set(true);
      }
    }

    this.setAdditionalValues(statement, ddsReference);

    this.delivery.set(delivery);
    this.hsCodes.set(hsCodes);

    this.isLoading.set(false);
  }

  private setAdditionalValues(statement: EudrDds.DdsDetails, ddsReference: EudrDds.DdsReference) {
    this.statusDisplayText.set(LocalizationUtils.getStatus(ddsReference.status));

    if (statement?.type === EudrDds.TypeEnum.NEW) {
      this.activityTypeDisplayText.set(
        LocalizationUtils.getEudrActivityType(statement.statement.activityType),
      );

      this.countryOfActivityName.set(
        this.commonService.getCountryName(statement.statement.countryOfActivity),
      );

      this.borderCrossCountryName.set(
        this.commonService.getCountryName(statement.statement.borderCrossCountry),
      );
    }

    this.statement.set(statement);
    this.ddsReference.set(ddsReference);
  }

  private async checkEudrCredentials(): Promise<void> {
    if (!this.isEudrExtensionEnabled()) {
      return null;
    }
    try {
      await this.eudrExtensionService.get();

      this.hasEudrCredentials.set(true);
    } catch (error) {
      if (error.status !== 404) {
        this.notificationService.showError(error);
      }
    }
  }

  public async onDelete(): Promise<void> {
    const contentHTML =
      this.ddsReference().status === EudrDds.StatusEnum.PENDING
        ? $localize`:@@confirm.dds.deletestatement:Are you sure you want to delete this statement?`
        : `<span class="icon-18 mat-icon material-symbols-outlined warn-icon relative-top2">warning</span> ${$localize`:@@confirm.dds.deletestatementextended:<b>WARNING</b>: this action will delete the due diligence statement (DDS) from Interu, but not from TRACES NT.<br>Are you sure you want to continue?`}`;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: TextConstants.DELETE_CONFIRMATION,
        contentHTML,
        confirmButtonColor: "danger",
        confirmButtonText: TextConstants.DELETE,
        confirmButtonIcon: IconConstants.DELETE,
      },
    });

    dialogRef.afterClosed().subscribe(async (result: ConfirmDialogResponseEnum) => {
      const performDelete = async () => {
        await this.eudrDdsService.delete(this.ddsReference().id);

        this.notificationService.showSuccess($localize`:@@dds.statementremoved:Statement removed`);

        this.routerService.navigate(RouteEnum.ADMIN_EXTENSIONS_EUDR);
      };

      try {
        if (result === ConfirmDialogResponseEnum.CONFIRM) {
          await performDelete();
        }
      } catch (error) {
        await this.deleteErrorHander.handle(error, performDelete);
      }
    });
  }

  public async onSubmit(): Promise<void> {
    try {
      this.isSubmitting.set(true);

      const ddsReference = await this.eudrDdsService.submit(this.ddsReference().id);

      this.ddsReference.set(ddsReference);

      this.statusDisplayText.set(LocalizationUtils.getStatus(ddsReference.status));

      this.notificationService.showSuccess(
        $localize`:@@dds.statementsubmitted:Statement submitted`,
      );
    } catch {
      this.notificationService.showError(TextConstants.DEFAULT_ERROR_NOTIFICATION);
    } finally {
      this.isSubmitting.set(false);
    }
  }
}
